import React, { useState, useCallback, useMemo, useEffect, useRef, useContext } from 'react';
import { Modal, Button, Input, message } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import './GroupAvatarEditModal.less';
import { useTranslation } from "react-i18next";
import IconFont from '@/components/icon';
import ImageCropModal from './ImageCropModal';
import { getRealConfig } from "@/utils/tools";
import html2canvas from 'html2canvas'
import { FocusSDK } from "@/utils";

interface GroupAvatarEditModalProps {
    visible: boolean;
    onCancel: () => void;
    onSave: (avatarData: { text: string; color: string; imageBlob?: Blob, defaultAvatar?: string }) => void;
    currentAvatar?: string;
    currentText?: string;
    currentColor?: string;
    loading?: boolean;
    teamConfig?: any;
    avatar?: string;
}

const GroupAvatarEditModal: React.FC<GroupAvatarEditModalProps> = ({
    visible,
    onCancel,
    onSave,
    currentText = '',
    currentColor = '',
    loading = false,
    teamConfig,
    avatar = ""
}) => {
    const { t } = useTranslation("chat");

    // Ref for avatar div element
    const avatarRef = useRef<HTMLDivElement>(null);

    // 读取群组头像颜色配置
    const groupAvatarColorConfig = useMemo(() => {
        const groupAvatarColor = JSON.parse(getRealConfig(teamConfig, {}, "groupAvatarColor"));

        const groupAvatarColorList = groupAvatarColor.groupAvatarColorList.filter((e: any) => e.selected === 1);
        console.log("groupAvatarColorList===", groupAvatarColorList);
        return groupAvatarColorList;
    }, [teamConfig]);
    // 字符长度限制和截断的通用函数
    const truncateText = useCallback((text: string) => {
        let charLength = 0;
        let truncatedValue = '';

        for (let i = 0; i < text.length; i++) {
            const char = text.charAt(i);
            let charWeight = 1;

            // 判断是否为中文字符
            // if (/[\u4e00-\u9fa5]/.test(char)) {
            //     charWeight = 2; // 中文字符占2个长度
            // }

            // 判定规则：中文字符 + 中文标点/全角符号 占2个长度
            // 正则包含：
            // 1. 中文字符：\u4e00-\u9fa5
            // 2. 中文标点/全角符号：\u3000-\u303f（全角符号、标点）、\uff00-\uffef（全角ASCII、标点）、\u201c\u201d（双引号）、\u2018\u2019（单引号）、\u2013\u2014（破折号）、\u3001\u3002（顿号、句号）等
            // 3. @符号
            const doubleWidthRegex = /[\u4e00-\u9fa5\u3000-\u303f\uff00-\uffef\u201c\u201d\u2018\u2019\u2013\u2014\u3001\u3002\uff1b\uff1a\uff0c\uff1f\uff01\uff08\uff09\uff5b\uff5d\u300a\u300b\u3008\u3009\u3010\u3011@\uff20]/;
            if (doubleWidthRegex.test(char)) {
                charWeight = 2; // 中文字符、中文标点、全角符号、@（半角/全角）占2个长度
            }

            // 如果加上当前字符不超过限制，则添加到结果中
            if (charLength + charWeight <= 8) {
                charLength += charWeight;
                truncatedValue += char;
            } else {
                // 超出限制，停止添加字符
                break;
            }
        }

        return truncatedValue;
    }, []);

    const [inputText, setInputText] = useState(truncateText(currentText)); // 输入框的文本
    const [avatarText, setAvatarText] = useState(truncateText(currentText)); // 头像显示和保存的文本
    const [selectedColor, setSelectedColor] = useState(currentColor); // 默认绿色
    // 中文输入法状态管理
    const [isComposing, setIsComposing] = useState(false);

    // 图片裁切相关状态
    const [cropModalVisible, setCropModalVisible] = useState(false);
    const [selectedImageFile, setSelectedImageFile] = useState<File | null>(null);
    const [croppedImageBlob, setCroppedImageBlob] = useState<Blob | null>(null);
    const [avatarImageUrl, setAvatarImageUrl] = useState<string>(avatar);
    const [isGenerateBlob, setIsGenerateBlob] = useState(false);

    const [isSaving, setIsSaving] = useState(false);

    // 根据 colorCode 生成背景色的函数
    const getBgColor = useCallback((colorCode: string) => {
        if (!colorCode) return '#0073FF'; // 默认颜色
        // 分割颜色代码
        const colors = colorCode.split(',').map(color => color.trim());
        // 如果只有一个颜色或两个颜色相同，返回单色
        if (colors.length === 1 || (colors.length === 2 && colors[0] === colors[1])) {
            return colors[0];
        }
        // 如果有两个不同的颜色，返回左上到右下的渐变
        if (colors.length === 2) {
            return `linear-gradient(135deg, ${colors[0]} 0%, ${colors[1]} 100%)`;
        }
        // 默认返回第一个颜色
        return colors[0];
    }, []);

    // 当 groupAvatarColorConfig 加载完成后，设置默认选中第一个颜色
    // useEffect(() => {
    //     if (groupAvatarColorConfig && groupAvatarColorConfig.length > 0 && !currentColor) {
    //         setSelectedColor(groupAvatarColorConfig[0].colorCode);
    //     }
    // }, [groupAvatarColorConfig, currentColor]);

    const handleTextChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        const previousLength = inputText.length;
        const currentLength = value.length;

        if (isComposing) {
            setInputText(value);
        } else {
            setInputText(truncateText(value));
        }
        // 如果是删除操作，或者不在输入法组合状态（非中文输入），实时更新头像文案
        if (currentLength < previousLength || !isComposing) {
            const truncated = truncateText(value);
            setAvatarText(truncated);
        }

        //  输入文字时没有选背景色，默认选中第一个
        if (value !== "" && !selectedColor) {
            if (groupAvatarColorConfig && groupAvatarColorConfig.length > 0) {
                setSelectedColor(groupAvatarColorConfig[0].colorCode);
            }
        }
        // 无输入时，改成兜底头像
        if (value === "") {
            if (groupAvatarColorConfig && groupAvatarColorConfig.length > 0 && selectedColor) {
                const REACT_APP_OSS_BASE_URL = FocusSDK?.getApplicationInfo()?.ossBaseUrl;
                const iconImgUrl = REACT_APP_OSS_BASE_URL + groupAvatarColorConfig.find((item: any) => item.colorCode === selectedColor).iconImgUrl;
                setAvatarImageUrl(iconImgUrl);
            }
        }

    }, [inputText, truncateText, isComposing, selectedColor]);

    // 输入法开始组合时
    const handleCompositionStart = useCallback(() => {
        setIsComposing(true);
    }, []);

    // 输入法结束组合时（用户完成中文输入，包括空格确认）
    const handleCompositionEnd = useCallback(() => {
        setIsComposing(false);
        // 延迟一下确保inputText已经更新
        setTimeout(() => {
            const truncated = truncateText(inputText);
            setInputText(truncated); // 更新输入框显示的文本
            setAvatarText(truncated);
            if (truncated !== "" && !selectedColor) {
                if (groupAvatarColorConfig && groupAvatarColorConfig.length > 0) {
                    setSelectedColor(groupAvatarColorConfig[0].colorCode);
                }
            }
        }, 0);
    }, [inputText, truncateText, selectedColor]);


    const handleColorSelect = useCallback((color: string) => {
        setSelectedColor(color);
        const REACT_APP_OSS_BASE_URL = FocusSDK?.getApplicationInfo()?.ossBaseUrl;
        const iconImgUrl = REACT_APP_OSS_BASE_URL + groupAvatarColorConfig.find((item: any) => item.colorCode === color).iconImgUrl;
        setAvatarImageUrl(iconImgUrl);
    }, []);

    const handleSave = useCallback(async () => {
        setIsSaving(true);
        if(!avatarText.trim() && croppedImageBlob){
            console.log("gif头像");
            onSave({
                text: "",
                color: "",
                imageBlob: croppedImageBlob
            });
            return;
        }
        // 如果是自定义文字头像，先生成 blob 再传递
        if (avatarText.trim() && avatarRef.current) {
            setIsGenerateBlob(true);
            let imageBlob = null;
            try {
                // domtoimage库对中文标点更优化，但是生成的图片质量差
                // imageBlob = await new Promise<Blob>((resolve) => {
                //     domtoimage.toBlob(avatarRef.current, {
                //         bgcolor: null, // 透明背景
                //         scale: 1, // 高清渲染
                //         width: 90,
                //         height: 90
                //     }).then((blob: any) => {
                //         resolve(blob);
                //     }).catch((err: any) => {
                //         console.error('生成失败：', err);
                //     });
                // });


                // html2canvas方案，图片较清晰，但是中文标点支持差
                // const canvas = await html2canvas(avatarRef.current, {
                //     backgroundColor: null,
                //     scale: 2, // 提高清晰度
                //     width: 90,
                //     height: 90,
                //     useCORS: true
                // });

                // // 将 canvas 转换为 blob
                // imageBlob = await new Promise<Blob>((resolve) => {
                //     canvas.toBlob((blob) => {
                //         resolve(blob!);
                //     }, 'image/png', 0.9);
                // });
                // setIsGenerateBlob(false);
                // onSave({
                //     text: avatarText.trim(),
                //     color: selectedColor,
                //     imageBlob: imageBlob || undefined
                // });


                // html-to-image方案 图片清晰且中文标点支持友好
                import("html-to-image").then(({ toBlob }) => {
                    toBlob(avatarRef.current as HTMLDivElement, {
                        width: 90,
                        height: 90,
                        pixelRatio: 2 // 可改为 window.devicePixelRatio * 2 进一步提升高清度
                    }).then((blob) => {
                        if (blob) {
                            setIsGenerateBlob(false);
                            onSave({
                                text: avatarText.trim(),
                                color: selectedColor,
                                imageBlob: blob || undefined
                            });
                        }else{
                            message.error('生成头像失败，请重试');
                        }
                    });
                });

            // setIsGenerateBlob(false);
            // onSave({
            //     text: avatarText.trim(),
            //     color: selectedColor,
            //     imageBlob: imageBlob || undefined,
            // });
        } catch (error) {
            console.error('生成头像 blob 失败:', error);
            message.error('生成头像失败，请重试');
            setIsSaving(false);
            return;
        }
        } else {
        // 如果是图片头像，直接传递
        console.log("图片头像", selectedColor, avatarImageUrl);
        onSave({
            text: "",
            color: selectedColor,
            defaultAvatar: avatarImageUrl
        });
        }
    }, [avatarText, selectedColor, croppedImageBlob, onSave]);

const handleCancel = useCallback(() => {
    // 重置为初始状态
    setAvatarText(currentText);
    setInputText("");
    // setSelectedColor('#0073FF,#0073FF');
    setCroppedImageBlob(null);
    setAvatarImageUrl('');
    setSelectedImageFile(null);
    setCropModalVisible(false);
    onCancel();
}, [currentText, onCancel]);

const modalFooterView = (
    <div>
        <Button
            style={{
                border: "1px solid #dee0e3",
                borderRadius: " 4px",
                color: "#1B1B1B"
            }}
            onClick={handleCancel}
            className="cancelBtn"
        >
            {t("cancelText")}
        </Button>
        <Button
            disabled={isSaving}
            type="primary"
            style={{
                border: "none",
                background: "@primary-color",
                borderRadius: "4px",
                fontSize: "14px",
                color: "#ffffff"
            }}
            onClick={handleSave}
        >
            {t("determine")}
        </Button>
    </div>
);

const selectImg = () => {
    const input = document.createElement("INPUT");
    document.body.appendChild(input);
    input.setAttribute("type", "file");
    input.setAttribute("accept", "image/jpeg,image/png,image/bmp,image/gif");
    document.body.appendChild(input);
    input.onchange = (ev: any) => {
        if (ev.target.files && ev.target.files[0]) {
            const file = ev.target.files[0];
            console.log("选择文件===", file);

            // 验证文件类型
            const validTypes = ['image/jpeg', 'image/png', 'image/bmp', 'image/gif'];
            if (!validTypes.includes(file.type)) {
                message.error('请选择jpeg、png、bmp或gif格式的图片');
                return;
            }

            // 验证文件大小 (4MB)
            const maxSize = 4 * 1024 * 1024;
            if (file.size > maxSize) {
                message.error('图片大小不能超过4MB');
                return;
            }

            // 如果是 gif，就直接转为 blob 并调用 onSave方法，否则打开裁切弹窗
            if (file.type === 'image/gif') {
                // GIF 文件直接转换为 blob 并调用 onSave
                // 创建预览URL
                const imageUrl = URL.createObjectURL(file);
                setAvatarText("");
                setInputText("");
                setSelectedColor("");
                setAvatarImageUrl(imageUrl);
                setCroppedImageBlob(file);
                setCropModalVisible(false);
            } else {
                // 其他图片格式打开裁切弹窗
                setSelectedImageFile(file);
                setCropModalVisible(true);
            }
        }
    };
    input.click();
    document.body.removeChild(input);
}

// 图片裁切确认回调
const handleCropConfirm = useCallback((croppedBlob: Blob) => {
    // setCroppedImageBlob(croppedBlob);
    console.log("测试防抖===4");
    onSave({
        text: "",
        color: "",
        imageBlob: croppedBlob || undefined
    });
    // 创建预览URL
    // const imageUrl = URL.createObjectURL(croppedBlob);
    // setAvatarImageUrl(imageUrl);
    // setCropModalVisible(false);
}, []);

// 图片裁切取消回调
const handleCropCancel = useCallback(() => {
    setCropModalVisible(false);
    setSelectedImageFile(null);
}, []);

return (
    <>
        <Modal
            centered={true}
            title={<span style={{ fontWeight: "bold" }}>修改群头像</span>}
            visible={visible}
            onCancel={handleCancel}
            maskClosable={false}
            footer={modalFooterView}
            wrapClassName="group-avatar-edit-modal"
        >
            <div className='modal-body'>
                <div className='left'>
                    <div className='avatarWrap'>
                        <div
                            ref={avatarRef}
                            className={`avatar ${isGenerateBlob ? "" : "avatar-mask"}`}
                            style={{
                                backgroundImage: avatarImageUrl && avatarText.trim() === "" ? `url("${avatarImageUrl}")` : undefined,
                                backgroundColor: avatarText === "" ? 'transparent' : getBgColor(selectedColor),
                                backgroundSize: '90px 90px',
                                backgroundRepeat: 'no-repeat',
                                backgroundPosition: 'center'
                            }}
                        >
                            {avatarText && <div className='avatarText'>{avatarText}</div>}
                        </div>
                        <div className='camera' onClick={selectImg}><IconFont type='iconvector2' />
                        </div>
                    </div>
                    {/* <div
                            className='avatar'
                            style={{
                                width: "90px",
                                height: "90px",
                                backgroundImage: avatarImageUrl ? `url("${avatarImageUrl}")` : undefined,
                                backgroundColor: avatarText === "" ? 'transparent' : getBgColor(selectedColor),
                                backgroundSize: '90px 90px',
                                backgroundRepeat: 'no-repeat',
                                backgroundPosition: 'center'
                            }}
                        /> */}
                </div>
                <div className='right'>
                    <div className='customText'>
                        <div className='title'>自定义文字</div>
                        <Input
                            type="text"
                            allowClear
                            value={inputText}
                            onChange={handleTextChange}
                            onCompositionStart={handleCompositionStart}
                            onCompositionEnd={handleCompositionEnd}
                        />

                    </div>
                    <div className='color'>
                        <div className='title'>选择颜色</div>
                        <div className='colorGroup'>
                            {groupAvatarColorConfig && groupAvatarColorConfig.map((item: any) => (
                                <div
                                    key={item.colorCode}
                                    className={`colorItem ${selectedColor === item.colorCode ? 'selected' : ''}`}
                                    style={{ backgroundColor: getBgColor(item.colorCode) }}
                                    onClick={() => handleColorSelect(item.colorCode)}
                                >
                                    {selectedColor === item.colorCode && (
                                        <IconFont type='iconic_pc_im_Finish' />
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </Modal>
        {/* 图片裁切弹窗 */}
        <ImageCropModal
            visible={cropModalVisible}
            onCancel={handleCropCancel}
            onConfirm={handleCropConfirm}
            imageFile={selectedImageFile}
        />
    </>
);
};

export default GroupAvatarEditModal;

